import CloudUpload from '@mui/icons-material/CloudUpload';
import YouTube from '@mui/icons-material/YouTube';
import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import {
  Snackbar, Alert, SpeedDial, SpeedDialAction, SpeedDialIcon,
} from '@mui/material';
import { FiberManualRecord } from '@mui/icons-material';
import { completeCheck, FileCheckResult, mapResultToErrorText } from '../../utils/fileHelper';
import YoutubeDialog from '../organisms/Dialogs/YoutubeDialog';
import { TrackingCategory, TrackingTranscriptionParams, trackingService } from '../../services';
import { AppConstantsContext } from '../../context';
import { JobType } from '../../entities/transcription';

export interface FileUploadSpeedDialProps {
  allowMultipleFiles?: boolean;
  allowedFileTypes?: string[];
  maximumFileSizeBytes?: number;
  onFiles: (files: FileList) => void;
  onYoutube: (url: string) => void;
  onRecord: () => void;
  defaultOpen?: boolean;
}

/**
 * Speed Dial Component to serve as UI to upload files
 * for transcription as alternative to file drop (e.g. on mobile)
 * buttons for YouTube, normal file upload
 */
const FileUploadSpeedDial: React.FC<FileUploadSpeedDialProps> = ({
  onFiles,
  allowMultipleFiles,
  maximumFileSizeBytes = Infinity,
  allowedFileTypes = [],
  onYoutube,
  onRecord,
  defaultOpen = false,
}) => {
  const { t } = useTranslation();
  const { jobType } = useContext(AppConstantsContext);
  const [open, setOpen] = useState(defaultOpen);
  const [yTDialogOpen, setYTDialogOpen] = useState(false);
  const [error, setError] = useState<FileCheckResult>(FileCheckResult.OK);
  const fileInputRef = useRef<HTMLInputElement>();

  const onFilesSubmit = useCallback(
    async (files?: FileList) => {
      setError(FileCheckResult.OK);

      const checkResult = await completeCheck(
        allowMultipleFiles,
        allowedFileTypes,
        maximumFileSizeBytes,
        files,
      );
      if (checkResult !== FileCheckResult.OK) {
        setError(checkResult);
        return;
      }

      onFiles(files);
    },
    [onFiles, allowedFileTypes, allowMultipleFiles],
  );

  const setTrackingEvent = (input: string) => {
    const trackingValues = {
      input,
      location: TrackingTranscriptionParams.location.songbook,
    };
    trackingService.valueObjectEvent(
      TrackingCategory.Transcription,
      TrackingTranscriptionParams.action.start,
      {
        ...trackingValues,
      },
    );
    // console.log(TrackingTranscriptionParams.action.start, { ...trackingValues });
  };

  const errorText = useMemo(() => mapResultToErrorText(error, t, maximumFileSizeBytes), [error, t]);

  return (
    <>
      <SpeedDial
        ariaLabel="SpeedDial basic fileUpload"
        icon={<SpeedDialIcon />}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
      >
        {' '}
        <SpeedDialAction
          key="upload"
          sx={{ bgcolor: 'primary.main' }}
          icon={<CloudUpload />}
          FabProps={{
            id: 'btn_speed_dial_file',
          }}
          id="btn_speed_dial_file"
          onClick={() => {
            fileInputRef.current.click();
          }}
          title={t('home.input.drop_file')}
        />
        {jobType !== JobType.TranscribeScan && (
          <SpeedDialAction
            key="youtube"
            sx={{ bgcolor: 'primary.main' }}
            icon={<YouTube />}
            FabProps={{ id: 'btn_speed_dial_youtube' }}
            id="btn_speed_dial_youtube"
            onClick={() => {
              setYTDialogOpen(true);
            }}
            title={t('home.input.youtube_url')}
          />
        )}
        {jobType !== JobType.TranscribeScan && (
          <SpeedDialAction
            key="record"
            color="primary"
            sx={{ bgcolor: 'primary.main' }}
            icon={<FiberManualRecord />}
            FabProps={{ id: 'btn_speed_dial_record' }}
            id="btn_speed_dial_record"
            onClick={() => {
              setTrackingEvent(TrackingTranscriptionParams.input.microphone);
              onRecord();
            }}
            title={t('home.input.record_your_own')}
          />
        )}
      </SpeedDial>

      <input
        ref={fileInputRef}
        type="file"
        multiple={allowMultipleFiles}
        hidden
        accept={allowedFileTypes.join(',')}
        onInput={() => onFilesSubmit(fileInputRef.current.files)}
      />

      <YoutubeDialog
        open={yTDialogOpen}
        onCancel={() => setYTDialogOpen(false)}
        onConfirm={(url) => {
          setYTDialogOpen(false);
          onYoutube(url);
        }}
      />

      <Snackbar
        open={error !== FileCheckResult.OK}
        autoHideDuration={6000}
        onClose={() => setError(FileCheckResult.OK)}
      >
        <Alert onClose={() => setError(FileCheckResult.OK)} severity="error">
          {errorText}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FileUploadSpeedDial;
